import { ComponentSize } from '@eleventheye/asui';
import { NFL_TEAMS_TOTAL } from '@shared/constants';
import TeamHorizontalChart from 'components/teamhorizontalchart/TeamHorizontalChart';
import { calculateLengthValue } from 'helpers/ChartHelpers';
import useViewportDimensions from 'hooks/useViewportDimensions';
import React, { useCallback, useEffect, useState } from 'react';
import { TeamRankInfo } from 'types';
import styles from './Rank.module.scss';

export const DisplayType = {
  HomeTeam: styles.HomeTeam,
  Leader: styles.Leader,
  AwayTeam: styles.AwayTeam,
};

export type HorizontalLineProps = {
  className?: string;
  rankInfo: TeamRankInfo;
  displayType: 'HomeTeam' | 'Leader' | 'AwayTeam';
  chartBy?: 'Rank' | 'Value';
  side?: string;
  startLabel?: React.ReactNode;
  endLabel?: React.ReactNode;
};

const HorizontalLine: React.FC<HorizontalLineProps> = ({
  className = '',
  rankInfo,
  displayType,
  side,
  chartBy = 'Rank',
  startLabel,
  endLabel,
}) => {
  const baseCls = `${styles.HLinePadding} ${className} `;
  const { isAppMobile } = useViewportDimensions();

  const [lengthValue, setLengthValue] = useState<string>('0');

  const chartByRank = useCallback((rankInfo: TeamRankInfo) => {
    const percentage = 100 - Math.floor((rankInfo.teamRank * 100) / NFL_TEAMS_TOTAL);
    // in case value is zero, add a small % to be shown
    setLengthValue(`${Math.min(percentage + 3, 100)}`);
  }, []);

  const chartByValue = useCallback((rankInfo: TeamRankInfo) => {
    const percentage = (rankInfo.teamValue * 100) / rankInfo.bestTeam!.value;
    setLengthValue(`${Math.min(percentage + 3, 100)}`);
  }, []);

  const updateChart = useCallback((chartBy: 'Rank' | 'Value', rankInfo: TeamRankInfo, isAppMobile: boolean) => {
    if (chartBy === 'Value' && rankInfo.bestTeam) {
      chartByValue(rankInfo);
    } else if (chartBy === 'Rank') {
      chartByRank(rankInfo);
    } else {
      setLengthValue(calculateLengthValue(rankInfo, chartBy, isAppMobile ? 250 : 280));
    }
  }, []);

  useEffect(() => {
    if (!rankInfo) return;

    const timerId = setTimeout(() => {
      updateChart(chartBy, rankInfo, isAppMobile);
    }, 300);

    return () => clearTimeout(timerId);
  }, [rankInfo, displayType, chartBy, updateChart, isAppMobile]);

  const getDimensionStyle = () => {
    const widthValue = isAppMobile ? '250px' : '280px';
    return { maxWidth: widthValue, width: widthValue, minWidth: widthValue };
  };

  return (
    <TeamHorizontalChart
      className={baseCls}
      direction={side === 'SideLeft' ? 'left' : 'right'}
      teamId={parseInt(rankInfo.teamID)}
      style={getDimensionStyle()}
      value={parseFloat(lengthValue)}
      startLabel={startLabel}
      endLabel={endLabel}
      size={displayType === 'Leader' ? ComponentSize.Small : ComponentSize.Medium}
    />
  );
};

export default HorizontalLine;
